import { SearchRounded as SearchIcon } from '@mui/icons-material';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridPaginationModel } from '@mui/x-data-grid';
import prettyBytes from 'pretty-bytes';
import { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { BestBlockChartCard } from './components/BestBlockChartCard';
import { FinalizedBlockChartCard } from './components/FinalizedBlockChartCard';
import { MemoryChartCard } from './components/MemoryChartCard';
import { PeerCountChartCard } from './components/PeerCountChartCard';
import { VersionChartCard } from './components/VersionChartCard';
import { VirtualMachineChartCard } from './components/VirtualMachineChartCard';
import { trpc } from './utils/trpc';

const App = () => {
  const [chain, setChain] = useState<string | null>(null);
  const [showOffline, setShowOffline] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const { data: nodes, isLoading } = trpc.node.list.useQuery({
    chain,
    showOffline,
    search: debouncedSearch,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
  });

  const { data: chains } = trpc.chain.list.useQuery({
    showOffline,
  });

  return (
    <Stack padding={4} spacing={2}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={2}>
        <Typography variant={'h5'}>{'Mosaic Chain Telemetry'}</Typography>
        <FormControlLabel
          checked={showOffline}
          onChange={(_, checked) => setShowOffline(checked)}
          control={<Checkbox />}
          label={'Show offline'}
        />
      </Stack>
      <Tabs value={chain} onChange={(_, value) => setChain(value)}>
        <Tab label={'All'} value={null} />
        {chains?.map((chain) => (
          <Tab
            label={
              <Stack direction={'row'} alignItems={'center'} spacing={1}>
                <Typography variant={'inherit'}>{chain.name}</Typography>
                <Chip label={chain.count} />
              </Stack>
            }
            value={chain.name}
            key={chain.name}
          />
        ))}
      </Tabs>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <VirtualMachineChartCard chain={chain} showOffline={showOffline} />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <BestBlockChartCard chain={chain} showOffline={showOffline} />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <FinalizedBlockChartCard chain={chain} showOffline={showOffline} />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <MemoryChartCard chain={chain} showOffline={showOffline} />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PeerCountChartCard chain={chain} showOffline={showOffline} />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <VersionChartCard chain={chain} showOffline={showOffline} />
        </Grid>
      </Grid>
      <TextField
        label={'Search'}
        value={search}
        size={'small'}
        variant={'filled'}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position={'start'}>
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      <DataGrid
        filterMode={'server'}
        paginationMode={'server'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={isLoading}
        rowCount={nodes?.count ?? -1}
        rows={nodes?.data}
        getRowId={(row) => row.networkId}
        columns={[
          {
            field: 'status',
            headerName: 'Status',
            valueGetter: (_value, row) => row.status === 'online',
            type: 'boolean',
            valueFormatter: (_value, row) => (row.status === 'online' ? 'Online' : 'Offline'),
          },
          {
            field: 'networkId',
            headerName: 'Network ID',
            type: 'string',
          },
          {
            field: 'name',
            headerName: 'Name',
            type: 'string',
          },
          {
            field: 'bestBlock',
            headerName: 'Best block',
            type: 'number',
            valueGetter: (_, row) => (row.bestBlock ? row.bestBlock.height : null),
            valueFormatter: (_, row) => (row.bestBlock ? `#${row.bestBlock.height} (${row.bestBlock.hash})` : null),
          },
          {
            field: 'finalizedBlock',
            headerName: 'Finalized block hash',
            type: 'number',
            valueGetter: (_, row) => (row.finalizedBlock ? row.finalizedBlock.height : null),
            valueFormatter: (_, row) =>
              row.finalizedBlock ? `#${row.finalizedBlock.height} (${row.finalizedBlock.hash})` : null,
          },
          {
            field: 'cpu',
            headerName: 'CPU',
            type: 'string',
          },
          {
            field: 'coreCount',
            headerName: 'Number of cores',
            type: 'number',
          },
          {
            field: 'memory',
            headerName: 'RAM',
            type: 'number',
            valueFormatter: (_value, row) => prettyBytes(row.memory),
          },
          {
            field: 'uploadBandwidth',
            headerName: 'Upload bandwidth',
            type: 'number',
            valueFormatter: (_, row) =>
              typeof row.uploadBandwidth === 'number'
                ? `${prettyBytes(row.uploadBandwidth, {
                    bits: true,
                  })} / s`
                : null,
          },
          {
            field: 'downloadBandwidth',
            headerName: 'Download bandwidth',
            type: 'number',
            valueFormatter: (_, row) =>
              typeof row.downloadBandwidth === 'number'
                ? `${prettyBytes(row.downloadBandwidth, {
                    bits: true,
                  })} / s`
                : null,
          },
          {
            field: 'peerCount',
            headerName: 'Peers',
            type: 'number',
          },
          {
            field: 'txpoolFuture',
            headerName: 'Future transactions',
            type: 'number',
          },
          {
            field: 'txpoolReady',
            headerName: 'Ready transactions',
            type: 'number',
          },
          {
            field: 'chain',
            headerName: 'Chain',
            type: 'string',
          },
          {
            field: 'isVirtualMachine',
            headerName: 'Virtual machine',
            type: 'boolean',
          },
          {
            field: 'genesisHash',
            headerName: 'Genesis hash',
            type: 'string',
          },
          {
            field: 'linuxDistro',
            headerName: 'Linux distro',
            type: 'string',
          },
          {
            field: 'linuxKernel',
            headerName: 'Linux kernel',
            type: 'string',
          },
          {
            field: 'targetOs',
            headerName: 'Operating System',
            type: 'string',
          },
          {
            field: 'targetArch',
            headerName: 'Architecture',
            type: 'string',
          },
          {
            field: 'targetEnv',
            headerName: 'Environment',
            type: 'string',
          },
          {
            field: 'implementation',
            headerName: 'Implementation',
            type: 'string',
          },
          {
            field: 'cpuHashrateScore',
            headerName: 'CPU Hashrate Score',
            type: 'number',
          },
          {
            field: 'diskRandomWriteScore',
            headerName: 'Disk Random Write Score',
            type: 'number',
          },
          {
            field: 'diskSequentialWriteScore',
            headerName: 'Disk Sequential Write Score',
            type: 'number',
          },
          {
            field: 'memoryMemcpyScore',
            headerName: 'Memory Memcpy Score',
            type: 'number',
          },
          {
            field: 'parallelCpuCores',
            headerName: 'Parallel CPU Cores',
            type: 'number',
          },
          {
            field: 'parallelCpuHashrateScore',
            headerName: 'Parallel CPU Hashrate Score',
            type: 'number',
          },
          {
            field: 'version',
            headerName: 'Version',
            type: 'string',
          },
          {
            field: 'startupTime',
            headerName: 'Startup time',
            type: 'dateTime',
          },
          {
            field: 'updatedAt',
            headerName: 'Updated at',
            type: 'dateTime',
          },
        ]}
      />
    </Stack>
  );
};

export default App;
